import { useMemo } from "react";
import { MapPin, Calendar, DollarSign, Clock, Send } from "lucide-react";
import styles from "./InternalWorkOfferCard.module.scss";
import { isExternalWorkOffer } from "../../utils/jobboard/isExternalOffer.ts";

export interface InternalWorkOffer {
  id: string;
  user?: {
    username?: string;
    image_profile?: string;
    type?: string;
    razon_social?: string;
    name?: string;
    last_name?: string;
  };
  skill?: Array<{
    skill?: {
      name?: string;
    };
  }>;
  workZones?: Array<{
    jurisdiccion?: {
      name?: string;
    };
    city?: string;
  }>;
  startDate?: string;
  activeUntil?: string;
  salary?: string;
  salaryType?: string;
  immediateStart?: boolean;
  description?: string;
  createdAt?: string;
}

interface InternalWorkOfferCardProps {
  offer: InternalWorkOffer;
  onApply: (offer: InternalWorkOffer) => void;
  onViewDetail?: (offer: InternalWorkOffer) => void;
}

const formatPostedTime = (createdAt?: string) => {
  if (!createdAt) return "Reciente";
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return "Reciente";
  const diff = Date.now() - createdDate.getTime();
  if (diff < 60 * 1000) return "Hace instantes";
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000));
    return `Hace ${minutes} min`;
  }
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    return `Hace ${hours} h`;
  }
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  return `Hace ${days} día${days === 1 ? "" : "s"}`;
};

const formatSalary = (salary?: string, salaryType?: string) => {
  if (!salary || salary === "0.00" || Number.parseFloat(salary) === 0) {
    return "A convenir";
  }
  const base = Math.floor(Number.parseFloat(salary)).toLocaleString("es-AR");
  switch (salaryType) {
    case "Hour":
      return `$${base} / hora`;
    case "Day":
      return `$${base} / día`;
    case "Week":
      return `$${base} / semana`;
    case "Month":
      return `$${base} / mes`;
    case "Job":
      return `$${base} / proyecto`;
    default:
      return `$${base}`;
  }
};

const formatDateLabel = (date?: string) => {
  if (!date) return "Sin dato";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "Sin dato";
  return parsed.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
};

const InternalWorkOfferCard = ({ offer, onApply, onViewDetail }: InternalWorkOfferCardProps) => {
  const companyLabel = useMemo(() => {
    if (offer?.user?.type === "COMPANY") {
      return offer.user?.razon_social ?? "Empresa";
    }
    const name = offer.user?.name ?? "";
    const lastName = offer.user?.last_name ?? "";
    const full = `${name} ${lastName}`.trim();
    return full || "Publicador";
  }, [offer.user]);

  const skillLabels = useMemo(
    () =>
      (offer.skill ?? [])
        .map((item) => item?.skill?.name)
        .filter(Boolean)
        .slice(0, 3) as string[],
    [offer.skill]
  );

  const remainingSkills =
    (offer.skill?.length ?? 0) - skillLabels.length > 0 ? (offer.skill?.length ?? 0) - skillLabels.length : 0;

  const primaryZone =
    offer.workZones?.[0]?.jurisdiccion?.name ?? offer.workZones?.[0]?.city ?? "Ubicación a confirmar";

  const postedLabel = formatPostedTime(offer.createdAt);
  const salaryLabel = formatSalary(offer.salary, offer.salaryType);
  const startLabel = offer.immediateStart ? "Inicio inmediato" : formatDateLabel(offer.startDate);
  const activeUntilLabel = formatDateLabel(offer.activeUntil);

  const description = offer.description ?? "";
  const trimmedDescription = description.length > 160 ? `${description.substring(0, 160)}…` : description;

  return (
    <article className={styles.card}>
      <header className={styles.header}>
        <div className={styles.company}>
          <div className={styles.companyName}>
            {companyLabel}
            {isExternalWorkOffer(offer as any) && (
              <span className={styles.externalBadge}>Búsqueda externa</span>
            )}
          </div>
          <span className={styles.time}>{postedLabel}</span>
        </div>
      </header>

      {skillLabels.length > 0 && (
        <div className={styles.skills}>
          {skillLabels.map((label) => (
            <span key={label} className={styles.skillChip}>
              {label}
            </span>
          ))}
          {remainingSkills > 0 && <span className={styles.skillChip}>+{remainingSkills}</span>}
        </div>
      )}

      {trimmedDescription && <p className={styles.description}>{trimmedDescription}</p>}

      <div className={styles.meta}>
        <div className={styles.metaItem}>
          <MapPin size={16} />
          <span>{primaryZone}</span>
        </div>
        <div className={styles.metaItem}>
          <Calendar size={16} />
          <span>{startLabel}</span>
        </div>
        <div className={styles.metaItem}>
          <DollarSign size={16} />
          <span>{salaryLabel}</span>
        </div>
        <div className={styles.metaItem}>
          <Clock size={16} />
          <span>Visible hasta {activeUntilLabel}</span>
        </div>
      </div>

      <footer className={styles.footer}>
        <button type="button" className={styles.secondaryButton} onClick={() => onViewDetail?.(offer)}>
          Ver detalle
        </button>
        <button type="button" className={styles.primaryButton} onClick={() => onApply(offer)}>
          <Send size={16} />
          Postularme
        </button>
      </footer>
    </article>
  );
};

export default InternalWorkOfferCard;

